import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FilesService } from 'src/app/core/services/files.service';
import { ManagementUser } from '../../core/models/management-user.model';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { faChevronDown, faChevronRight, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import introJs from 'intro.js';
import { secureFileSharingIntroSteps } from './secure-file-sharing.intro-steps';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';

interface SharedUser {
  email: string;
  firstName: string;
  lastName: string;
  userID?: number;
  fileID?: number;
}

interface FileData {
  file: File | null;
  fileName: string;
  fileType: string;
  fileSize: number;
  expirationDate?: number;
  uploadDate?: number;
  ownerUserID?: number;
  showDetails?: boolean;
  sharedUsers: SharedUser[];
  newShareEmail: string;
  uploaded?: boolean;
  fileID?: number;
  description?: string;
  ownerUser?: ManagementUser;
}

@Component({
    selector: 'wf-secure-data-sharing',
    templateUrl: './secure-data-sharing.component.html',
    styleUrls: ['./secure-data-sharing.component.css'],
    standalone: false
})
export class SecureDataSharingComponent implements OnInit {
  managementUser: ManagementUser | null = null;
  files: FileData[] = [];
  maxFileSize: number = 10 * 1024 * 1024;
  ownerUserId: number | undefined = undefined;

  searchTerm: string = '';

  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';

  filesIcon = CATEGORY_ICONS['files']
  faChevronDown = faChevronDown;
  faChevronRight = faChevronRight;
  helpIcon = faQuestionCircle;

  introTour: any;

  readonly MAX_FILES = 10;

  constructor(
    private filesService: FilesService,
    private sessionService: SessionStorageService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.managementUser = this.sessionService.userData();
    this.ownerUserId = this.managementUser?.userID;

    this.route.data.subscribe(data => {
      const resolvedFiles = data['resolveData'];
      if (resolvedFiles && Array.isArray(resolvedFiles)) {
        resolvedFiles.forEach((file: any) => {
          const modifiedFileName = file.fileName.replace(/^\d{14}_/, '');
          this.files.push({
            file: null,
            fileName: modifiedFileName,
            fileType: file.fileType,
            fileSize: file.fileSize,
            expirationDate: file.expirationDate,
            uploadDate: file.uploadDate,
            ownerUserID: file.ownerUserID,
            showDetails: false,
            sharedUsers: file.sharedUsers ? file.sharedUsers.map((user: any) => ({
              email: user.email,
              firstName: user.firstName,
              lastName: user.lastName,
              userID: user.userID
            })) : [],
            newShareEmail: '',
            uploaded: true,
            fileID: file.fileID,
            description: file.description,
            ownerUser: file.ownerUser
          });
        });
      }
    });
  }

  ngAfterViewInit(): void {
    this.setupIntroTour();
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }

      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  setupIntroTour() {
    this.introTour = introJs();
    this.introTour.setOptions({
      steps: secureFileSharingIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
      showBullets: true,
      showSkip: false,
    });
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(secureFileSharingIntroSteps);
    this.introTour.setOptions({ steps: filteredSteps });
    this.introTour.start();
  }

  get filteredFiles(): FileData[] {
    if (!this.searchTerm) {
      return this.files;
    }
    const lowerSearchTerm = this.searchTerm.toLowerCase();
    return this.files.filter(file => {
      const fileNameMatch = file.fileName.toLowerCase().includes(lowerSearchTerm);
      let ownerMatches = false;
      if (file.ownerUser) {
        const owner = file.ownerUser;
        const firstNameMatch = owner.firstName.toLowerCase().includes(lowerSearchTerm);
        const lastNameMatch = owner.lastName.toLowerCase().includes(lowerSearchTerm);
        const emailMatch = owner.email!.toLowerCase().includes(lowerSearchTerm);
        const districtMatch = owner.districtName!.toLowerCase().includes(lowerSearchTerm);
        const schoolMatch = owner.school ? owner.school.toLowerCase().includes(lowerSearchTerm) : false;
        ownerMatches = firstNameMatch || lastNameMatch || emailMatch || districtMatch || schoolMatch;
      }
      return fileNameMatch || ownerMatches;
    });
  }

  get sortedFilteredFiles(): FileData[] {
    let filtered = this.filteredFiles.slice();
    if (!this.sortColumn) {
      return filtered;
    }
    filtered.sort((a, b) => {
      let aVal = a[this.sortColumn as keyof FileData];
      let bVal = b[this.sortColumn as keyof FileData];

      if (aVal === undefined && bVal === undefined) {
        return 0;
      }
      if (aVal === undefined) {
        return 1;
      }
      if (bVal === undefined) {
        return -1;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return this.sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return this.sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return 0;
    });
    return filtered;
  }

  sortBy(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  get canUploadMoreFiles(): boolean {
    // Customer service users have unlimited uploads
    if (this.managementUser?.isCustomerServiceUser() || this.managementUser?.isPartnerSuccessUser()) {
      return true;
    }
    // Only count files owned by the current user
    return this.files.filter(f => f.uploaded && f.ownerUserID === this.managementUser?.userID).length < this.MAX_FILES;
  }

  onFileSelected(event: any): void {
    const selectedFiles = event.target.files;
    // Only count files owned by the current user
    const uploadedCount = this.files.filter(f => f.uploaded && f.ownerUserID === this.managementUser?.userID).length;
    // Customer service users: no limit
    if (this.managementUser?.isCustomerServiceUser() || this.managementUser?.isPartnerSuccessUser()) {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file: File = selectedFiles[i];
        if (file.size > this.maxFileSize) {
          alert(`File \"${file.name}\" exceeds the 10MB limit.`);
        } else {
          const fileData: FileData = {
            file: file,
            fileName: file.name,
            fileType: file.type,
            fileSize: file.size,
            showDetails: false,
            sharedUsers: [],
            newShareEmail: '',
            uploaded: false
          };
          this.files.push(fileData);
          const tempId = this.files.length - 1;
          this.uploadFile(tempId);
        }
      }
      event.target.value = '';
      return;
    }
    if (uploadedCount >= this.MAX_FILES) {
      alert(`You can only upload up to ${this.MAX_FILES} files.`);
      event.target.value = '';
      return;
    }
    let filesToAdd = Math.min(selectedFiles.length, this.MAX_FILES - uploadedCount);
    for (let i = 0; i < filesToAdd; i++) {
      const file: File = selectedFiles[i];
      if (file.size > this.maxFileSize) {
        alert(`File \"${file.name}\" exceeds the 10MB limit.`);
      } else {
        const fileData: FileData = {
          file: file,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          showDetails: false,
          sharedUsers: [],
          newShareEmail: '',
          uploaded: false
        };
        this.files.push(fileData);
        const tempId = this.files.length - 1;
        this.uploadFile(tempId);
      }
    }
    if (selectedFiles.length > filesToAdd) {
      alert(`You can only upload up to ${this.MAX_FILES} files. Only the first ${filesToAdd} file(s) were added.`);
    }
    event.target.value = '';
  }

  removeFile(fileId: number): void {
    const index = this.files.findIndex(file => file.fileID === fileId);
    if (index !== -1) {
      const confirmDelete = confirm(`Are you sure you want to delete the file "${this.files[index].fileName}"?`);
      if (confirmDelete) {
        const fileData = this.files[index];
        if (fileData.uploaded && fileData.fileID) {
          this.filesService.deleteFile(fileData.fileID).subscribe(
            (response: any) => {
              this.files.splice(index, 1);
            },
            (error: any) => {
              console.error('Error deleting file:', error);
            }
          );
        } else {
          this.files.splice(index, 1);
        }
      }
    }
  }

  toggleDetails(fileId: number): void {
    const file = this.files.find(f => f.fileID === fileId || (f.file && this.files.indexOf(f) === fileId));
    if (file) {
      file.showDetails = !file.showDetails;
    }
  }

  addSharedEmail(fileId: number): void {
    const file = this.files.find(f => f.fileID === fileId || (f.file && this.files.indexOf(f) === fileId));
    if (!file) return;

    const email = file.newShareEmail.trim();
    if (email) {
      if (file.uploaded && file.fileID) {
        this.filesService.shareFile(file.fileID, email, new Date()).subscribe(
          (response: any) => {
            if (response.firstName && response.lastName && response.email && response.fileID && response.userID) {
              file.sharedUsers.push({ firstName: response.firstName, lastName: response.lastName, email: response.email, fileID: response.fileID, userID: response.userID });
            } else if (response.error) {
              alert(`Error sharing file with ${email}: ${response.error}`);
              console.error(`Error sharing file with ${email}.`);
            }
          },
          (error: any) => {
            alert(`Error sharing file with ${email}: ${error.error.error}`);
            console.error(`Error sharing file with ${email}:`, error);
          }
        );
      } else {
        console.error(`Cannot share file "${file.fileName}" because it has not been uploaded yet or is missing an ID.`);
      }
      file.newShareEmail = '';
    }
  }

  removeSharedEmail(fileId: number, userIndex: number): void {
    const file = this.files.find(f => f.fileID === fileId || (f.file && this.files.indexOf(f) === fileId));
    if (!file) return;

    const sharedUser = file.sharedUsers[userIndex];
    this.filesService.unshareFile(file.fileID!, sharedUser.userID!).subscribe(
      (response: any) => {
        file.sharedUsers.splice(userIndex, 1);
      },
      (error: any) => {
        console.error('Error unsharing file:', error);
        alert('Error unsharing file. Please try again.');
      }
    );
  }

  shareAction(fileId: number): void {
    const file = this.files.find(f => f.fileID === fileId || (f.file && this.files.indexOf(f) === fileId));
    if (!file) return;

    if (!file.showDetails) {
      file.showDetails = true;
      setTimeout(() => {
        const elementId = (file.fileID !== undefined) ? `emailInput${file.fileID}` : `emailInput${this.files.indexOf(file)}`;
        const input = document.getElementById(elementId) as HTMLInputElement;
        if (input) {
          input.focus();
        }
      }, 0);
    } else {
      const elementId = (file.fileID !== undefined) ? `emailInput${file.fileID}` : `emailInput${this.files.indexOf(file)}`;
      const input = document.getElementById(elementId) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }
  }

  uploadFile(index: number): void {
    const fileData = this.files[index];
    if (!fileData.uploaded && fileData.file && this.ownerUserId !== undefined) {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 30);
      this.filesService.uploadFile(this.ownerUserId, fileData.file, fileData.description, expirationDate).subscribe(
        (response: any) => {
          fileData.uploaded = true;
          fileData.fileID = response.fileID;
          fileData.uploadDate = response.uploadDate;
          fileData.expirationDate = response.expirationDate;
          fileData.ownerUserID = response.ownerUserID;
          fileData.fileType = response.fileType;
          fileData.fileSize = response.fileSize;
            fileData.ownerUser = {
            userID:      response.ownerUserID,
            firstName:   response.uploaderFirstName,
            lastName:    response.uploaderLastName,
            email:       response.uploaderEmail,
            districtName: response.uploaderDistrictName,
            school:      response.uploaderSchool
          } as ManagementUser;


        },
        (error: any) => {
          console.error('Error uploading file:', error);
          alert(`Error uploading file "${fileData.fileName}".`);
        }
      );
    } else {
      alert(`File "${fileData.fileName}" has already been uploaded or is missing.`);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Only count files owned by the current user
    const uploadedCount = this.files.filter(f => f.uploaded && f.ownerUserID === this.managementUser?.userID).length;
    // Customer service users: no limit
    if (this.managementUser?.isCustomerServiceUser && this.managementUser.isCustomerServiceUser()) {
      if (event.dataTransfer && event.dataTransfer.files) {
        for (let i = 0; i < event.dataTransfer.files.length; i++) {
          const file: File = event.dataTransfer.files[i];
          if (file.size > this.maxFileSize) {
            alert(`File \"${file.name}\" exceeds the 10MB limit.`);
          } else {
            const fileData: FileData = {
              file: file,
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              showDetails: false,
              sharedUsers: [],
              newShareEmail: '',
              uploaded: false
            };
            this.files.push(fileData);
            const tempId = this.files.length - 1;
            this.uploadFile(tempId);
          }
        }
      }
      return;
    }
    if (uploadedCount >= this.MAX_FILES) {
      alert(`You can only upload up to ${this.MAX_FILES} files.`);
      return;
    }
    let filesToAdd = Math.min(event.dataTransfer?.files.length || 0, this.MAX_FILES - uploadedCount);
    for (let i = 0; i < filesToAdd; i++) {
      const file: File = event.dataTransfer!.files[i];
      if (file.size > this.maxFileSize) {
        alert(`File \"${file.name}\" exceeds the 10MB limit.`);
      } else {
        const fileData: FileData = {
          file: file,
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          showDetails: false,
          sharedUsers: [],
          newShareEmail: '',
          uploaded: false
        };
        this.files.push(fileData);
        const tempId = this.files.length - 1;
        this.uploadFile(tempId);
      }
    }
    if (event.dataTransfer!.files.length > filesToAdd) {
      alert(`You can only upload up to ${this.MAX_FILES} files. Only the first ${filesToAdd} file(s) were added.`);
    }
  }

  downloadFile(file: FileData): void {
    if (file.fileID) {
      this.filesService.downloadFile(file.fileID).subscribe(
        (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = file.fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);
        },
        (error: any) => {
          console.error("Error downloading file", error);
          alert("Error downloading file.");
        }
      );
    } else {
      alert("File not available for download.");
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }

  getElementId(file: FileData): string {
    return file.fileID !== undefined ? `${file.fileID}` : `${this.files.indexOf(file)}`;
  }
}
